import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Calendar, Plus, Save, Trash2, Send, Loader2, ChevronLeft, ChevronRight, Stethoscope, Heart, Crown, Clock, X, Repeat,
} from 'lucide-react';
import ReceptionLayout from '../components/ReceptionLayout';
import DrawerShell from '../components/DrawerShell';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useTenant } from '../contexts/TenantContext';
import { toast } from 'sonner';
import { toDateIST } from '../utils/date';

const API = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: API, withCredentials: true });

const ROLE_META = {
  doctor:     { label: 'Doctor',     color: '#0f766e', icon: Stethoscope },
  nurse:      { label: 'Nurse',      color: '#7c3aed', icon: Heart },
  head_nurse: { label: 'Head Nurse', color: '#dc2626', icon: Crown },
};

const dayLabel = (d) => d.toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', timeZone: 'Asia/Kolkata' });
const isoDate = (d) => toDateIST(d);

function startOfWeek(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - x.getDay() + (x.getDay() === 0 ? -6 : 1));
  return x;
}

export default function ReceptionRosterPage() {
  const { tenant } = useTenant();
  const brand = tenant?.branding?.primary_color || '#0f766e';
  const [anchor, setAnchor] = useState(() => startOfWeek(new Date()));
  const [view, setView] = useState('week'); // 'week' | 'month'
  const [roleFilter, setRoleFilter] = useState('');
  const [entries, setEntries] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editor, setEditor] = useState(null);  // {date, role?} when creating
  const [publishing, setPublishing] = useState(false);

  const days = useMemo(() => {
    const total = view === 'week' ? 7 : 28;
    return Array.from({ length: total }, (_, i) => {
      const d = new Date(anchor);
      d.setDate(d.getDate() + i);
      return d;
    });
  }, [anchor, view]);

  const fromIso = isoDate(days[0]);
  const toIso = isoDate(days[days.length - 1]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = { from_date: fromIso, to_date: toIso };
      if (roleFilter) params.role = roleFilter;
      const { data } = await ax.get('/api/clinic-ops/roster', { params });
      setEntries(data.entries || []);
    } catch (_) { toast.error('Could not load roster'); }
    setLoading(false);
  }, [fromIso, toIso, roleFilter]);

  useEffect(() => {
    reload();
    ax.get('/api/clinic-ops/staff').then(({ data }) => setStaff(data.staff || [])).catch(() => {});
  }, [reload]);

  const byDayRole = useMemo(() => {
    const m = {};
    for (const e of entries) {
      const k = `${e.date}|${e.role}`;
      (m[k] = m[k] || []).push(e);
    }
    return m;
  }, [entries]);

  const publishWeek = async () => {
    if (!window.confirm(`Publish all roster entries from ${fromIso} to ${toIso}?`)) return;
    setPublishing(true);
    try {
      await ax.post('/api/clinic-ops/roster/publish', { from_date: fromIso, to_date: toIso, publish: true });
      toast.success('Roster published — visible on landing page');
      reload();
    } catch (_) { toast.error('Publish failed'); }
    setPublishing(false);
  };

  const unpublishWeek = async () => {
    if (!window.confirm('Unpublish all entries in this range?')) return;
    try {
      await ax.post('/api/clinic-ops/roster/publish', { from_date: fromIso, to_date: toIso, publish: false });
      toast.success('Unpublished');
      reload();
    } catch (_) { toast.error('Failed'); }
  };

  const deleteEntry = async (id) => {
    if (!window.confirm('Delete this shift?')) return;
    try {
      await ax.delete(`/api/clinic-ops/roster/${id}`);
      reload();
    } catch (_) { toast.error('Delete failed'); }
  };

  return (
    <ReceptionLayout
      pageTitle="Duty Roster"
      pageSubtitle="Doctor · Nurse · Head Nurse schedules — publish weekly to drive landing-page availability"
      rightActions={
        <>
          <Button variant="outline" size="sm" onClick={unpublishWeek} data-testid="roster-unpublish-btn">Unpublish</Button>
          <Button size="sm" onClick={publishWeek} disabled={publishing} data-testid="roster-publish-btn">
            {publishing ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1.5" />}
            Publish
          </Button>
        </>
      }
    >
      <div className="p-6 max-w-[1600px] mx-auto space-y-4" data-testid="reception-roster">
        {/* Toolbar */}
        <div className="bg-white border rounded-xl p-3 flex items-center gap-2 flex-wrap shadow-sm">
          <Button variant="outline" size="sm" onClick={() => {
            const d = new Date(anchor); d.setDate(d.getDate() - (view === 'week' ? 7 : 28));
            setAnchor(d);
          }} data-testid="roster-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <div className="text-sm font-bold text-zinc-900 min-w-[200px]" style={{ fontFamily: 'Manrope' }}>
            {anchor.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', timeZone: 'Asia/Kolkata' })}
            {' – '}
            {days[days.length - 1].toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' })}
          </div>
          <Button variant="outline" size="sm" onClick={() => {
            const d = new Date(anchor); d.setDate(d.getDate() + (view === 'week' ? 7 : 28));
            setAnchor(d);
          }} data-testid="roster-next"><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" onClick={() => setAnchor(startOfWeek(new Date()))}>Today</Button>
          <div className="ml-2 inline-flex rounded-lg border overflow-hidden">
            {['week','month'].map(v => (
              <button key={v} onClick={() => setView(v)} className={`px-3 py-1 text-xs font-semibold ${view===v ? 'bg-zinc-900 text-white' : 'bg-white text-zinc-700 hover:bg-zinc-50'}`} data-testid={`roster-view-${v}`}>{v}</button>
            ))}
          </div>
          <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="border rounded-md px-2 py-1 text-sm" data-testid="roster-role-filter">
            <option value="">All roles</option>
            <option value="doctor">Doctor</option>
            <option value="nurse">Nurse</option>
            <option value="head_nurse">Head Nurse</option>
          </select>
          <div className="ml-auto text-[11px] text-zinc-500">{entries.length} shifts · {entries.filter(e=>e.published).length} published</div>
        </div>

        {/* Grid */}
        <div className="bg-white border rounded-xl overflow-hidden shadow-sm">
          <div className="grid border-b" style={{ gridTemplateColumns: `repeat(${view==='week'?7:7}, minmax(0,1fr))` }}>
            {days.slice(0, 7).map(d => (
              <div key={d.toISOString()} className="px-3 py-2 text-center text-[11px] uppercase tracking-wider font-bold text-zinc-500 border-r last:border-r-0">
                {dayLabel(d)}
              </div>
            ))}
          </div>
          {loading ? (
            <div className="py-12 text-center text-zinc-400"><Loader2 className="h-5 w-5 mx-auto animate-spin" /></div>
          ) : (
            <div className="grid auto-rows-min" style={{ gridTemplateColumns: `repeat(${view==='week'?7:7}, minmax(0,1fr))` }}>
              {days.map((d, i) => {
                const dateIso = isoDate(d);
                const all = ['doctor', 'head_nurse', 'nurse'].flatMap(r => byDayRole[`${dateIso}|${r}`] || []);
                return (
                  <div key={dateIso + i} className="min-h-[120px] border-r border-b last:border-r-0 p-1.5 space-y-1 bg-zinc-50/40 hover:bg-blue-50/30 transition" data-testid={`day-${dateIso}`}>
                    {view === 'month' && (
                      <div className="text-[10px] text-zinc-500 font-bold">{d.getDate()}</div>
                    )}
                    {all.map(e => {
                      const meta = ROLE_META[e.role] || ROLE_META.doctor;
                      const Icon = meta.icon;
                      return (
                        <div
                          key={e.id}
                          onClick={() => setEditor({ ...e })}
                          className={`group rounded p-1.5 text-[11px] cursor-pointer border-l-4 ${e.published ? 'bg-emerald-50' : 'bg-amber-50'} hover:shadow`}
                          style={{ borderColor: meta.color }}
                          data-testid={`shift-${e.id}`}
                        >
                          <div className="flex items-center gap-1">
                            <Icon className="h-3 w-3" style={{ color: meta.color }} />
                            <span className="font-semibold text-zinc-900 truncate flex-1">{e.staff_name}</span>
                            <button onClick={(ev) => { ev.stopPropagation(); deleteEntry(e.id); }} className="text-zinc-300 hover:text-red-500 opacity-0 group-hover:opacity-100"><X className="h-2.5 w-2.5" /></button>
                          </div>
                          <div className="text-[10px] text-zinc-500 flex items-center gap-0.5"><Clock className="h-2.5 w-2.5" />{e.shift_start} – {e.shift_end}</div>
                        </div>
                      );
                    })}
                    <button
                      onClick={() => setEditor({ date: dateIso, role: 'doctor' })}
                      className="w-full text-[10px] text-zinc-400 hover:text-zinc-700 hover:bg-white border border-dashed border-zinc-200 hover:border-zinc-300 rounded py-1 transition"
                      data-testid={`add-shift-${dateIso}`}
                    >
                      <Plus className="h-3 w-3 inline" /> Add
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {editor && (
        <ShiftEditor
          initial={editor}
          staff={staff}
          onClose={() => setEditor(null)}
          onSaved={() => { setEditor(null); reload(); }}
        />
      )}
    </ReceptionLayout>
  );
}

function ShiftEditor({ initial, staff, onClose, onSaved }) {
  const isNew = !initial.id;
  const [form, setForm] = useState({
    date: initial.date || '',
    role: initial.role || 'doctor',
    staff_id: initial.staff_id || '',
    staff_name: initial.staff_name || '',
    specialisation: initial.specialisation || '',
    shift_start: initial.shift_start || '09:00',
    shift_end: initial.shift_end || '13:00',
    location: initial.location || '',
    slot_minutes: initial.slot_minutes || 15,
    max_appointments: initial.max_appointments || 0,
    notes: initial.notes || '',
    published: initial.published || false,
  });
  const [saving, setSaving] = useState(false);
  // Recurring pattern: when enabled, instead of a single insert we expand
  // across selected weekdays in the chosen range.
  const [recurring, setRecurring] = useState(false);
  const [recurrence, setRecurrence] = useState({
    from_date: initial.date || '',
    to_date: '',
    days_of_week: [],  // 0=Mon..6=Sun
  });

  const filteredStaff = staff.filter(s => (s.role || '').toLowerCase() === form.role || (form.role === 'doctor' ? s.role === 'doctor' : (form.role.includes('nurse') && (s.role || '').toLowerCase().includes('nurse'))));

  const onPick = (id) => {
    const u = staff.find(s => s.id === id);
    if (!u) return;
    setForm(f => ({ ...f, staff_id: u.id, staff_name: u.name, specialisation: u.specialization || f.specialisation }));
  };

  const toggleDay = (n) => {
    setRecurrence(r => ({
      ...r,
      days_of_week: r.days_of_week.includes(n)
        ? r.days_of_week.filter(d => d !== n)
        : [...r.days_of_week, n].sort(),
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      if (recurring && isNew) {
        if (!recurrence.from_date || !recurrence.to_date) { toast.error('Pick a recurrence range'); setSaving(false); return; }
        if (recurrence.days_of_week.length === 0) { toast.error('Pick at least one day of week'); setSaving(false); return; }
        if (!form.staff_id) { toast.error('Pick a staff member'); setSaving(false); return; }
        const body = { ...form, ...recurrence };
        const { data } = await ax.post('/api/clinic-ops/roster/recurring', body);
        toast.success(`Created ${data.inserted} shifts${data.skipped_duplicates ? ` · skipped ${data.skipped_duplicates} dupes` : ''}`);
      } else if (isNew) {
        if (!form.staff_id || !form.staff_name) { toast.error('Pick a staff member'); setSaving(false); return; }
        await ax.post('/api/clinic-ops/roster', form);
        toast.success('Shift created');
      } else {
        await ax.patch(`/api/clinic-ops/roster/${initial.id}`, form);
        toast.success('Shift updated');
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const roleMeta = ROLE_META[form.role] || ROLE_META.doctor;
  const RoleIcon = roleMeta.icon;
  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  return (
    <DrawerShell
      open
      onClose={onClose}
      title={isNew ? (recurring ? 'Add recurring shifts' : 'Add shift') : 'Edit shift'}
      subtitle={isNew ? 'Plan a single date or a repeating pattern' : `${initial.staff_name} · ${initial.date}`}
      eyebrow={`Roster · ${roleMeta.label}`}
      accent={roleMeta.color}
      icon={<RoleIcon className="h-5 w-5" />}
      width="md"
      testId="shift-editor"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="shift-save">
            {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
            {isNew ? (recurring ? 'Create pattern' : 'Create') : 'Save'}
          </Button>
        </>
      }
    >
      <div className="p-5 space-y-4">
        {isNew && (
          <div className="rounded-lg border bg-white p-3 flex items-center gap-3">
            <Repeat className="h-4 w-4 text-violet-600 flex-shrink-0" />
            <div className="flex-1">
              <div className="text-xs font-bold text-zinc-900">Recurring pattern</div>
              <div className="text-[11px] text-zinc-500">Repeat the shift on selected weekdays across a date range</div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" checked={recurring} onChange={(e) => setRecurring(e.target.checked)} className="sr-only peer" data-testid="shift-recurring-toggle" />
              <div className="w-9 h-5 bg-zinc-200 peer-checked:bg-violet-600 rounded-full peer transition" />
              <span className="absolute left-0.5 top-0.5 bg-white w-4 h-4 rounded-full transition peer-checked:translate-x-4" />
            </label>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          {!recurring && (
            <div>
              <label className="text-[10px] uppercase font-bold text-zinc-500">Date</label>
              <Input type="date" value={form.date} onChange={(e) => setForm(f => ({ ...f, date: e.target.value }))} data-testid="shift-date" />
            </div>
          )}
          <div className={recurring ? 'col-span-2' : ''}>
            <label className="text-[10px] uppercase font-bold text-zinc-500">Role</label>
            <select className="w-full border rounded-md px-2 py-1.5 text-sm" value={form.role} onChange={(e) => setForm(f => ({ ...f, role: e.target.value }))} data-testid="shift-role">
              <option value="doctor">Doctor</option>
              <option value="nurse">Nurse</option>
              <option value="head_nurse">Head Nurse</option>
            </select>
          </div>
        </div>

        {recurring && (
          <div className="rounded-lg border bg-violet-50/40 border-violet-200 p-3 space-y-2.5" data-testid="recurring-block">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] uppercase font-bold text-violet-700">From</label>
                <Input type="date" value={recurrence.from_date} onChange={(e) => setRecurrence(r => ({ ...r, from_date: e.target.value }))} data-testid="recurring-from" />
              </div>
              <div>
                <label className="text-[10px] uppercase font-bold text-violet-700">To</label>
                <Input type="date" value={recurrence.to_date} onChange={(e) => setRecurrence(r => ({ ...r, to_date: e.target.value }))} data-testid="recurring-to" />
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase font-bold text-violet-700 block mb-1">Days of week</label>
              <div className="flex gap-1 flex-wrap">
                {dayNames.map((d, i) => (
                  <button
                    key={d}
                    onClick={() => toggleDay(i)}
                    className={`px-2.5 py-1 rounded-md text-xs font-bold transition ${
                      recurrence.days_of_week.includes(i)
                        ? 'bg-violet-600 text-white'
                        : 'bg-white text-zinc-600 border hover:bg-violet-100'
                    }`}
                    data-testid={`recurring-day-${i}`}
                  >{d}</button>
                ))}
              </div>
            </div>
          </div>
        )}

        <div>
          <label className="text-[10px] uppercase font-bold text-zinc-500">Staff</label>
          <select className="w-full border rounded-md px-2 py-1.5 text-sm" value={form.staff_id} onChange={(e) => onPick(e.target.value)} data-testid="shift-staff">
            <option value="">— pick —</option>
            {filteredStaff.map(s => <option key={s.id} value={s.id}>{s.name} {s.specialization ? `· ${s.specialization}` : ''}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] uppercase font-bold text-zinc-500">Start</label>
            <Input type="time" value={form.shift_start} onChange={(e) => setForm(f => ({ ...f, shift_start: e.target.value }))} data-testid="shift-start" />
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-zinc-500">End</label>
            <Input type="time" value={form.shift_end} onChange={(e) => setForm(f => ({ ...f, shift_end: e.target.value }))} data-testid="shift-end" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] uppercase font-bold text-zinc-500">Slot mins</label>
            <Input type="number" min={5} step={5} value={form.slot_minutes} onChange={(e) => setForm(f => ({ ...f, slot_minutes: +e.target.value }))} data-testid="shift-slot-mins" />
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-zinc-500">Max appts</label>
            <Input type="number" min={0} value={form.max_appointments} onChange={(e) => setForm(f => ({ ...f, max_appointments: +e.target.value }))} data-testid="shift-max-appts" />
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase font-bold text-zinc-500">Location</label>
          <Input placeholder="e.g. OPD-2, Ward A…" value={form.location} onChange={(e) => setForm(f => ({ ...f, location: e.target.value }))} data-testid="shift-location" />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.published} onChange={(e) => setForm(f => ({ ...f, published: e.target.checked }))} data-testid="shift-published" />
          Publish on landing page
        </label>
      </div>
    </DrawerShell>
  );
}
